import { Controller } from '@hotwired/stimulus';
import consumer from '../channels/consumer';

// Holds the concurrent edition lock for the open template. Subscribing to the
// TemplateEditingChannel keeps the lock alive (heartbeat); leaving the page
// releases it. The eviction modal opens on three triggers:
//   - "takeover_requested": a waiting user asked for a graceful handover. We
//     still hold the lock, so we save (custom) then release it for them.
//   - "evicted": a hard force happened (fallback). The lock already moved; the
//     final save relies on the server grace window.
//   - a late autosave got a 409 (the lock is already gone).
//
// Two modes, set per page:
//   - "save" (custom editor): a final content autosave runs, then the modal
//     confirms it ("Enregistré").
//   - "warn" (cerfa / variables): nothing can be autosaved, the modal states
//     the unsaved changes are lost.
//
// Timing invariants — these constants are coupled, keep them ordered:
//   HEARTBEAT_MS < Templates::EditLock::TTL
//     A lock not refreshed within TTL is considered stale and freely taken by
//     anybody. Browsers throttle timers in hidden tabs to >= 60s (Chrome and
//     Safari, then once a minute after a few minutes), so the margin has to
//     absorb that, not just the network round trip. visibilitychange also
//     forces a refresh as soon as the tab comes back to the foreground.
//   SAVE_TIMEOUT_MS < GRANT_TIMEOUT_MS (lock_takeover_controller.js)
//     The waiting user must not fall back to the hard force while this editor
//     is still saving.
//   SAVE_TIMEOUT_MS < Templates::EditLock::GRACE_TTL
//     The final save has to land inside the server-side grace window.
const SAVE_TIMEOUT_MS = 8000;
const HEARTBEAT_MS = 20000;

export default class extends Controller {
  static targets = [
    'modal', 'titleName', 'bodyName',
    'iconWarning', 'iconSuccess',
    'bannerSaving', 'bannerSaved', 'bannerLost', 'bannerError',
    'button',
  ];

  static values = {
    templateId: String,
    userId: Number,
    dashboardPath: String,
    mode: { type: String, default: 'warn' },
  };

  connect() {
    if (!this.templateIdValue) return;

    this.phase = null;
    this.requesterId = null;
    this.onLockLost = this.onLockLost.bind(this);
    this.onEditorSaved = this.onEditorSaved.bind(this);
    this.onSaveFailed = this.onSaveFailed.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    window.addEventListener('editor:lock-lost', this.onLockLost);
    window.addEventListener('editor:saved', this.onEditorSaved);
    window.addEventListener('editor:save-failed', this.onSaveFailed);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.subscription = consumer.subscriptions.create(
      { channel: 'TemplateEditingChannel', template_id: this.templateIdValue },
      { received: (data) => this.onMessage(data) },
    );

    this.heartbeat = setInterval(() => this.#sendHeartbeat(), HEARTBEAT_MS);
  }

  disconnect() {
    this.clearHeartbeat();
    this.clearSaveTimeout();
    if (this.subscription) consumer.subscriptions.remove(this.subscription);
    window.removeEventListener('editor:lock-lost', this.onLockLost);
    window.removeEventListener('editor:saved', this.onEditorSaved);
    window.removeEventListener('editor:save-failed', this.onSaveFailed);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  // A hidden tab has its timers throttled well past the heartbeat interval, so
  // refresh immediately on the way back rather than waiting for the next tick.
  onVisibilityChange() {
    if (document.visibilityState === 'visible') this.#sendHeartbeat();
  }

  #sendHeartbeat() {
    if (this.subscription) this.subscription.perform('heartbeat');
  }

  onMessage(data) {
    if (!data) return;

    if (data.action === 'evicted' && data.evicted_user_id === this.userIdValue) {
      // Hard takeover (fallback): the lock has already moved to the new editor.
      this.evict(data.by, data.by_firstname);
    } else if (
      data.action === 'takeover_requested' &&
      data.requester_id !== this.userIdValue
    ) {
      // Negotiated takeover: we still hold the lock, save then release.
      this.handleTakeoverRequest(data);
    }
  }

  // A late autosave received a 409: the lock already belongs to someone else.
  onLockLost() {
    if (this.phase === 'saving') {
      this.setPhase('error');
    } else if (this.phase === null) {
      // Silent loss with no eviction broadcast: the content could not be saved.
      this.openModal();
      this.setPhase('error');
    }
  }

  // window "editor:saved" — the final autosave resolved successfully.
  onEditorSaved() {
    if (this.phase === 'saving') this.setPhase('saved');
  }

  // window "editor:save-failed" — the final autosave was rejected (non-2xx) or
  // never reached the server. Fail straight away instead of letting the user
  // watch the spinner until SAVE_TIMEOUT_MS elapses.
  onSaveFailed() {
    if (this.phase === 'saving') this.setPhase('error');
  }

  evict(byName, byFirstname) {
    if (this.phase !== null) return;

    this.fillNames(byName, byFirstname);
    this.openModal();

    // We no longer hold the lock: stop the heartbeat and leave the channel.
    this.clearHeartbeat();
    this.teardownSubscription();

    if (this.modeValue === 'save') {
      this.setPhase('saving');
      // Trigger the final content save; the server grace window allows it.
      window.dispatchEvent(new CustomEvent('editor:save'));
      this.saveTimeout = setTimeout(() => {
        if (this.phase === 'saving') this.setPhase('error');
      }, SAVE_TIMEOUT_MS);
    } else {
      this.setPhase('lost');
    }
  }

  // The waiting user asked for a graceful handover. We still hold the lock, so
  // we save (custom only) then release it for them — no grace window needed.
  handleTakeoverRequest(data) {
    if (this.phase !== null) return;

    this.requesterId = data.requester_id;
    this.fillNames(data.requester_name, data.requester_firstname);
    this.openModal();
    // Keep the subscription: we release it explicitly once the save resolves.
    this.clearHeartbeat();

    if (this.modeValue === 'save') {
      this.setPhase('saving');
      window.dispatchEvent(new CustomEvent('editor:save'));
      this.saveTimeout = setTimeout(() => {
        if (this.phase === 'saving') this.setPhase('error');
      }, SAVE_TIMEOUT_MS);
    } else {
      this.setPhase('lost');
    }
  }

  setPhase(phase) {
    this.phase = phase;
    const saving = phase === 'saving';

    if (this.hasIconWarningTarget) this.iconWarningTarget.hidden = phase === 'saved';
    if (this.hasIconSuccessTarget) this.iconSuccessTarget.hidden = phase !== 'saved';
    if (this.hasBannerSavingTarget) this.bannerSavingTarget.hidden = !saving;
    if (this.hasBannerSavedTarget) this.bannerSavedTarget.hidden = phase !== 'saved';
    if (this.hasBannerLostTarget) this.bannerLostTarget.hidden = phase !== 'lost';
    if (this.hasBannerErrorTarget) this.bannerErrorTarget.hidden = phase !== 'error';
    if (this.hasButtonTarget) this.buttonTarget.disabled = saving;

    if (phase === 'saved' || phase === 'lost' || phase === 'error') {
      this.clearSaveTimeout();
      // Command the editor to stop autosaving for good. Deliberately a
      // different event from "editor:lock-lost", which travels the other way
      // (the editor notifying us that a save got a 409) — reusing one name for
      // both directions made this controller listen to itself.
      window.dispatchEvent(new CustomEvent('editor:stop-saving'));
      // Negotiated takeover: release the lock so the waiting user can enter
      // (even on error, so they are never left waiting).
      if (this.requesterId != null && this.subscription) {
        this.subscription.perform('release_for_takeover', {
          requester_id: this.requesterId,
        });
      }
      this.teardownSubscription();
    }
  }

  fillNames(byName, byFirstname) {
    if (byFirstname && this.hasTitleNameTarget) {
      this.titleNameTarget.textContent = byFirstname;
    }
    if (byName && this.hasBodyNameTarget) {
      this.bodyNameTarget.textContent = byName;
    }
  }

  openModal() {
    if (this.hasModalTarget) this.modalTarget.hidden = false;
  }

  goToDashboard() {
    window.location.href = this.dashboardPathValue || '/';
  }

  teardownSubscription() {
    if (this.subscription) consumer.subscriptions.remove(this.subscription);
    this.subscription = null;
  }

  clearHeartbeat() {
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = null;
  }

  clearSaveTimeout() {
    if (this.saveTimeout) clearTimeout(this.saveTimeout);
    this.saveTimeout = null;
  }
}
